import { createSelector } from '@reduxjs/toolkit'

import { ApplicationState } from 'redux/Stores/types'

import {
  ICustomEconomyMetric,
  IMetric,
  IMetricRawData,
  ISelectMetric,
  MetricStatus,
  TCustomizedMetric,
  TDefinitionSlice,
  TMetricDataPostBody,
  TMetricFilterOptions,
  TMetricsResult,
  isCustomEconomyMetric,
  isPercentageMetric,
  isStandardMetric
} from 'types/GlobalMetric'

import { TSlicesResult } from 'types/GlobalSlice'

import { selectAllAndLoading } from 'redux/selectors/FilterOptions'
import { selectByFactAndLoading } from 'redux/selectors/AttributeOptions'
import { selectEntitySlicesMapper } from 'redux/selectors/Slices'

import type { TAttributeOptionDto } from '@homepal/api-contract'

import { FilterOptionsBody } from 'types/GlobalAttributeOptions'

import { sortAlphabeticalAsc } from 'utils/sortHelper'
import { getMetricDataKey } from 'utils/metric'

const emptySlicesResult: TSlicesResult = {
  all: [],
  disabled: [],
  available: [],
  custom: [],
  standard: [],
  selection_mode: 'multi_optional'
}

const getVariantConfig = (metric: IMetric, variantId?: string | null) => {
  if (isCustomEconomyMetric(metric)) {
    const vId = variantId ?? metric.default_variant

    const selectedVariant = metric.variants.find((vr) => vr.id === vId)

    if (selectedVariant) {
      return {
        default_settings: selectedVariant.default_settings,
        distributions: isPercentageMetric(metric)
          ? []
          : selectedVariant.distributions,
        entity_id: selectedVariant.entity_id,
        fact_table_id: selectedVariant.fact_table_id,
        rolling_sums: selectedVariant.rolling_sums,
        unit: selectedVariant.unit,
        unit_prefixes: selectedVariant.unit_prefixes,

        // only relevant for custom economy metrics
        full_title: selectedVariant.full_title
      }
    }

    return undefined
  }

  return {
    default_settings: metric.default_settings,
    distributions: metric.distributions,
    entity_id: metric.entity_id,
    fact_table_id: metric.fact_table_id,
    rolling_sums: metric.rolling_sums,
    unit: metric.unit,
    unit_prefixes: metric.unit_prefixes
  }
}

const parseAllowedFilterOptions = (
  metric: ISelectMetric,
  optionsByFactTableId: {
    [factTableId: string]: TAttributeOptionDto[]
  },
  filterOptions: FilterOptionsBody
): TMetricFilterOptions => {
  if (!optionsByFactTableId[metric.fact_table_id]) {
    return {}
  }

  const allowedOptions: TMetricFilterOptions = {}

  optionsByFactTableId[metric.fact_table_id]
    .filter(
      (option) =>
        !option.disabled &&
        option.filter_attribute &&
        // NUMBER attributes use free input and need no value list
        (filterOptions[option.id]?.type === 'NUMBER' ||
          (option.type === 'STRING' && filterOptions[option.id]?.values))
    )
    .forEach((option) => {
      const numeric = filterOptions[option.id]?.type === 'NUMBER'

      allowedOptions[option.id] = {
        id: option.id,
        name: option.name,
        numeric,
        options: numeric ? [] : (filterOptions[option.id].values as string[])
      }
    })

  return allowedOptions
}

const selectIsLoading = createSelector(
  [
    // Input selectors:
    (state: ApplicationState) => state.MetricStore.fetched,
    (state: ApplicationState) => state.MetricStore.fetching
  ],
  (fetched, fetching): boolean => {
    return !fetched && fetching
  }
)

/**
 * The metric the results page leads with, and whether the backend has
 * answered yet.
 */
export const selectHeadlineMetric = createSelector(
  [(state: ApplicationState) => state.MetricStore.headline],
  (headline): { metric: TCustomizedMetric | null; resolved: boolean } => ({
    metric: headline.data?.available
      ? { id: headline.data.id, settings: headline.data.settings }
      : null,
    resolved: headline.resolved
  })
)

/** Whether the metric catalog has been fetched. */
export const selectMetricsFetched = (state: ApplicationState) =>
  state.MetricStore.fetched

export const selectCustomKpis = createSelector(
  [(state: ApplicationState) => state.MetricStore.data, selectIsLoading],
  (data, loading): [ICustomEconomyMetric[], boolean] => {
    if (loading) {
      return [[], true]
    }

    return [
      Object.values(data)
        .filter(isCustomEconomyMetric)
        .sort((a, b) => sortAlphabeticalAsc(a.title.sv, b.title.sv)),
      false
    ]
  }
)

// one instance, so the raw data tuple stays shallowly equal
export const NO_MISSING_SLICES: TDefinitionSlice[] = []

export const selectMetricRawData = createSelector(
  [
    (state: ApplicationState) => state.MetricStore.rawData,
    (_: ApplicationState, id: string) => id,
    (_: ApplicationState, _id: string, keyParams?: TMetricDataPostBody) =>
      keyParams
  ],
  (
    rawData,
    id: string,
    keyParams?: TMetricDataPostBody
  ): [IMetricRawData | null, boolean, TDefinitionSlice[]] => {
    const sliceKey = getMetricDataKey(keyParams)

    const keyData =
      id in rawData && sliceKey in rawData[id] ? rawData[id][sliceKey] : null

    if (keyData) {
      return [keyData.data, keyData.error, keyData.missingSlices]
    }

    return [null, false, NO_MISSING_SLICES]
  }
)

// select all metrics
export const selectMetrics = createSelector(
  [
    (state: ApplicationState) => state.MetricStore.data,
    selectIsLoading,
    (_: ApplicationState, lang: 'sv' | 'en') => lang
  ],
  (data, loading, lang): [IMetric[], boolean] => {
    if (loading) {
      return [[], true]
    }

    return [
      Object.values(data).sort((a, b) =>
        sortAlphabeticalAsc(a.title[lang], b.title[lang])
      ),
      false
    ]
  }
)

export const selectOneMetric = createSelector(
  [
    (state: ApplicationState) => state.MetricStore.data,
    selectIsLoading,
    (state: ApplicationState) => selectEntitySlicesMapper(state),
    (state: ApplicationState) => state.EntityStore.data,
    (_: ApplicationState, id: string) => id,
    (_: ApplicationState, id: string, lang: 'sv' | 'en') => lang,
    (
      _: ApplicationState,
      id: string,
      lang: 'sv' | 'en',
      variantId?: string | null
    ) => variantId
  ],
  (
    data,
    metricLoading,
    slicesMapper,
    entities,
    id,
    lang,
    variantId
  ): ISelectMetric | null => {
    if (metricLoading || !data[id]) {
      return null
    }

    const metric = data[id]

    const config = getVariantConfig(metric, variantId)

    if (!config) {
      return null
    }

    const slices = config.entity_id
      ? slicesMapper[config.entity_id] ?? emptySlicesResult
      : emptySlicesResult

    const entity = config.entity_id ? entities[config.entity_id] ?? null : null

    const selectMetric = {
      ...metric,
      ...config,
      entity,
      slices
    }

    if (isCustomEconomyMetric(selectMetric)) {
      selectMetric.variants = [...selectMetric.variants].sort((a, b) =>
        sortAlphabeticalAsc(a.title[lang], b.title[lang])
      )
    }

    return selectMetric
  }
)

export const selectEntityMetricsMapper = createSelector(
  [
    (state: ApplicationState) => state.EntityStore.data,
    (state: ApplicationState) => state.MetricStore.data,
    (_: ApplicationState, lang: 'sv' | 'en') => lang
  ],
  (entities, metrics, lang): { [entityId: string]: TMetricsResult } => {
    const mapper: { [entityId: string]: IMetric[] } = Object.fromEntries(
      Object.keys(entities).map((entityId) => [entityId, []])
    )

    Object.values(metrics)
      .filter(
        (metric): metric is IMetric & { entity_id: string } =>
          isStandardMetric(metric) && !!metric.entity_id
      )
      .sort((a, b) => sortAlphabeticalAsc(a.title[lang], b.title[lang]))
      .forEach((metric) => {
        (mapper[metric.entity_id] ??= []).push(metric)
      })

    return Object.fromEntries(
      Object.entries(mapper).map(([entityId, entityMetrics]) => [
        entityId,
        {
          all: entityMetrics,
          active: entityMetrics.filter((m) => m.status === MetricStatus.ACTIVE),
          inactive: entityMetrics.filter(
            (m) => m.status === MetricStatus.INACTIVE
          ),
          missing_data: entityMetrics.filter(
            (m) => m.status === MetricStatus.MISSING_DATA
          )
        }
      ])
    )
  }
)

export const selectMetricFilterOptions = createSelector(
  [
    // Input selectors:
    selectOneMetric,
    selectByFactAndLoading,
    selectAllAndLoading
  ],
  (
    metric,
    [optionsByFactTableId, attributesLoading],
    [filterOptions, filtersLoading]
  ): { data: TMetricFilterOptions; loading: boolean } => {
    if (!metric || attributesLoading || filtersLoading) {
      return { data: {}, loading: true }
    }

    return {
      data: parseAllowedFilterOptions(
        metric,
        optionsByFactTableId,
        filterOptions
      ),
      loading: false
    }
  }
)
